import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { useRef } from "react";
import { Skeleton } from "../Skeleton";
import styles from "./Tag.module.scss";
import type { TagProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Used to display a supplementary value or a key/value pair in relation to a
 * higher-level concept
 */
const Tag = ({
  value,
  label,
  as: asProp,
  labelAs: labelAsProp,
  valueAs: valueAsProp,
  isLoading,
  isBusy,
  className,
  ...restProps
}: TagProps) => {
  const labelId = useRef(nanoid(6));
  const Tag = asProp ?? "div";
  const LabelCustomNode = labelAsProp ?? "p";
  const ValueCustomNode = valueAsProp ?? "p";

  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isBusy}
    >
      <Tag
        className={cx("container", className)}
        {...restProps}
      >
        {label ? (
          <div
            className={cx("label")}
            data-role="tag-label"
          >
            <LabelCustomNode
              title={label}
              id={labelId.current}
            >
              {label}
            </LabelCustomNode>
          </div>
        ) : null}
        <ValueCustomNode
          className={cx("content", !label && "no-label")}
          {...(label ? { "aria-describedby": labelId.current } : {})}
          data-role="tag-content"
        >
          {value}
        </ValueCustomNode>
      </Tag>
    </Skeleton>
  );
};

export default Tag;
